/**
 * Mobile bottom sheet (10-presentation-and-ux.md): the sidebar collapses into
 * a draggable sheet with three snap states. Smart defaults keep the board
 * readable: the sheet rises for the pick phase and drops to the strip when a
 * wave starts. Desktop never activates this.
 */
import type { Phase } from '../core/types.js';
import { byId } from './dom.js';

export type SheetState = 'strip' | 'half' | 'full';

const HEIGHTS: Record<SheetState, string> = {
  strip: 'var(--sheet-strip)',
  half: '46vh',
  full: '85vh',
};

export class Sheet {
  private state: SheetState = 'half';
  private readonly side = byId('side');
  private lastPhase: Phase | null = null;
  private dragStartY: number | null = null;
  private dragStartHeight = 0;

  activate(): void {
    document.body.classList.add('sheet-mode');
    const grabber = byId('sheetGrabber');
    grabber.addEventListener('pointerdown', (e) => {
      this.dragStartY = e.clientY;
      this.dragStartHeight = this.side.getBoundingClientRect().height;
      grabber.setPointerCapture(e.pointerId);
    });
    grabber.addEventListener('pointermove', (e) => {
      if (this.dragStartY === null) return;
      const h = this.dragStartHeight + (this.dragStartY - e.clientY);
      this.side.style.height = `${Math.max(28, Math.min(window.innerHeight * 0.9, h))}px`;
    });
    const endDrag = (): void => {
      if (this.dragStartY === null) return;
      this.dragStartY = null;
      this.snapToNearest();
    };
    grabber.addEventListener('pointerup', endDrag);
    grabber.addEventListener('pointercancel', endDrag);
    grabber.addEventListener('click', () => {
      // Tap the grabber: cycle strip -> half -> strip (full needs a drag).
      this.set(this.state === 'strip' ? 'half' : 'strip');
    });
    this.set('strip');
  }

  private snapToNearest(): void {
    const h = this.side.getBoundingClientRect().height;
    const vh = window.innerHeight;
    const candidates: [SheetState, number][] = [
      ['strip', 44],
      ['half', vh * 0.46],
      ['full', vh * 0.85],
    ];
    let best: SheetState = 'half';
    let bestD = Infinity;
    for (const [state, target] of candidates) {
      const d = Math.abs(h - target);
      if (d < bestD) {
        bestD = d;
        best = state;
      }
    }
    this.set(best);
  }

  set(state: SheetState): void {
    this.state = state;
    this.side.style.height = HEIGHTS[state];
    this.side.classList.toggle('sheet-strip', state === 'strip');
  }

  /**
   * Phase-driven smart defaults (only on transitions): the sheet rises when
   * there are cards to pick or a result to read, and drops to the strip
   * whenever the player needs the board (building, watching the wave).
   */
  onPhase(phase: Phase): void {
    if (phase === this.lastPhase) return;
    this.lastPhase = phase;
    if (phase === 'choose' || phase === 'gameover') this.set('half');
    else this.set('strip');
  }
}
